export const getApiBaseUrl = (): string => {
    // Check if the current browser window is running strictly on local dev machine
    const isStrictLocalhost = 
        typeof window !== 'undefined' && 
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    if (!isStrictLocalhost) {
        const envUrl = process.env.REACT_APP_API_URL;
        // Only use envUrl if it is a valid remote URL (not localhost or 127.0.0.1)
        if (envUrl && !envUrl.includes('localhost') && !envUrl.includes('127.0.0.1')) {
            return envUrl.endsWith('/api') ? envUrl : `${envUrl.replace(/\/$/, '')}/api`;
        }
        return '/api';
    }

    // Only for local development on localhost or 127.0.0.1
    return `http://${window.location.hostname}:5000/api`;
};

export const getAiApiBaseUrl = (): string => {
    const isStrictLocalhost = 
        typeof window !== 'undefined' && 
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    if (!isStrictLocalhost) {
        const envUrl = process.env.REACT_APP_AI_API_URL;
        if (envUrl && !envUrl.includes('localhost') && !envUrl.includes('127.0.0.1')) {
            return envUrl;
        }
        return '/api-ai';
    }

    return `http://${window.location.hostname}:5000/api-ai`;
};

export const API_BASE_URL = getApiBaseUrl();
export const AI_API_BASE_URL = getAiApiBaseUrl();

/**
 * Robust fetch wrapper that handles authorization headers and token expiration redirects.
 */
export const apiFetch = async (endpoint: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('adminToken');

    // Dynamically resolve API base URL for current environment
    const baseUrl = getApiBaseUrl();

    // Normalize endpoint (ensure it starts with /)
    const url = endpoint.startsWith('http') ? endpoint : `${baseUrl}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;

    const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...options.headers,
    };

    try {
        const response = await fetch(url, {
            ...options,
            headers,
        });

        // Handle token expiration or unauthorized access
        if (response.status === 401 || response.status === 403) {
            console.warn('Authentication failure, redirecting to login...');
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');

            // Avoid infinite loops if already on login
            if (!window.location.pathname.includes('/admin/login')) {
                window.location.href = '/admin/login';
            }
            throw new Error('Unauthorized');
        }

        return response;
    } catch (error) {
        if (error instanceof Error && error.message === 'Unauthorized') {
            throw error;
        }
        // Fallback attempt via relative /api proxy if localhost:5000 fails
        if (typeof url === 'string' && url.includes(':5000/api')) {
            const fallbackUrl = url.replace(/http:\/\/[^/]+:5000\/api/, '/api');
            try {
                const fallbackResponse = await fetch(fallbackUrl, {
                    ...options,
                    headers,
                });
                return fallbackResponse;
            } catch {
                // Fallback attempt failed, throw original error below
            }
        }
        console.error(`API Fetch Error [${url}]:`, error);
        throw error;
    }
};
